import React, { FC, MouseEvent, useRef, useState } from "react";
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { Link } from "react-router-dom";
import { RootState } from "@store/store";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { ScreenSize } from "@constants/screen-size";
import { useSignOut } from "@hooks/useSignOut";
import { Mode } from '@store/homeSlice';

let counter = 0;

const AccountMenu: FC<{ onboardingMode?: boolean }> = ({ onboardingMode }) => {
  const menuId = useRef(`account-menu-${counter++}`);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isSmScreen = useMediaQuery({
    query: `(max-width: ${ScreenSize.md}px)`,
  });
  const isPatient = useSelector(
    (state: RootState) => state.auth.user?.isPatient,
  );
  const mode = useSelector((state: RootState) => state.home.mode);
  const onSignOut = useSignOut();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const signOut = async () => {
    handleClose();
    await onSignOut();
  };

  const showHelp = () => {
    handleClose();
    // Open chat directly; launcher visibility is controlled centrally by
    // route/form-factor logic (HomeRoutes).
    window.Argoli.Brevo.showHelp();
  }

  return (
    <>
      <Button
        id="account-menu-button"
        aria-controls={open ? menuId.current : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        <PersonOutlineOutlinedIcon />
      </Button>

      <Menu
        onClose={handleClose}
        open={open}
        id={menuId.current}
        anchorEl={anchorEl}
      >
        <MenuList>
          {!onboardingMode && !isPatient && mode === Mode.Caregiver && (
            <>
              <MenuItem component={Link} to="/settings/my-account" onClick={handleClose}>
                <ListItemIcon>
                  <PersonOutlineOutlinedIcon />
                </ListItemIcon>
                <ListItemText>{t("header.myAccountButton")}</ListItemText>
              </MenuItem>
              <MenuItem component={Link} to="/settings/billing" onClick={handleClose}>
                <ListItemIcon>
                  <CardMembershipIcon />
                </ListItemIcon>
                <ListItemText>{t("header.billingButton")}</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
          {isSmScreen && (
            <>
              <MenuItem onClick={showHelp}>
                <ListItemIcon>
                  <QuestionMarkOutlinedIcon />
                </ListItemIcon>
                <ListItemText>{t("header.helpButton")}</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem id="signout-button" onClick={signOut}>
            <ListItemIcon>
              <LogoutOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{t("header.signOutButton")}</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

export default AccountMenu;
